/** States & Motion — gradient banner, dividers, animation demos, alerts. */
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { body, Section } from '../shared';

// ─── Inline Alert ─────────────────────────────────────────────────────────────

function InlineAlert({
  message,
  variant,
}: {
  message: string;
  variant: 'info' | 'warning' | 'success';
}) {
  const config = {
    info: {
      icon: AlertCircle,
      color: 'var(--color-info)',
      bg: 'var(--color-info-subtle)',
      text: 'var(--color-info-text)',
    },
    warning: {
      icon: AlertCircle,
      color: 'var(--color-warning)',
      bg: 'var(--color-warning-subtle)',
      text: 'var(--color-warning-text)',
    },
    success: {
      icon: CheckCircle2,
      color: 'var(--color-success)',
      bg: 'var(--color-success-subtle)',
      text: 'var(--color-success-text)',
    },
  }[variant];
  const Icon = config.icon;

  return (
    <div
      className="flex items-center gap-3"
      style={{
        padding: '12px 16px',
        borderRadius: 12,
        background: config.bg,
        border: `1px solid color-mix(in srgb, ${config.color} 20%, transparent)`,
      }}
    >
      <Icon size={16} strokeWidth={2} style={{ color: config.color, flexShrink: 0 }} />
      <p style={{ ...body, fontSize: 13, color: config.text, fontWeight: 500 }}>{message}</p>
    </div>
  );
}

// ─── Tab content ──────────────────────────────────────────────────────────────

export default function StatesTab() {
  return (
    <>
      <Section label="Dividers">
        <div className="flex flex-col gap-6">
          <div>
            <p style={{ ...body, marginBottom: 8 }}>Solid</p>
            <div style={{ height: 1, background: 'var(--color-surface-border)' }} />
          </div>
          <div>
            <p style={{ ...body, marginBottom: 8 }}>Fade</p>
            <div
              style={{
                height: 1,
                background:
                  'linear-gradient(90deg, transparent, var(--color-surface-border), transparent)',
              }}
            />
          </div>
          <div>
            <p style={{ ...body, marginBottom: 8 }}>Brand gradient</p>
            <div
              style={{
                height: 2,
                borderRadius: 1,
                background: 'linear-gradient(90deg, #ff9b4a, #ff6eb1, #8a6cf0)',
                opacity: 0.3,
              }}
            />
          </div>
        </div>
      </Section>

      <Section label="Hover & Press">
        <p style={body}>
          All interactive surfaces use consistent motion:{' '}
          <code
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 13,
              background: 'var(--color-surface-muted)',
              padding: '2px 6px',
              borderRadius: 4,
            }}
          >
            0.2s
          </code>{' '}
          transitions with darker border (
          <code
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 13,
              background: 'var(--color-surface-muted)',
              padding: '2px 6px',
              borderRadius: 4,
            }}
          >
            --color-border-strong
          </code>
          ) and subtle shadow elevation on hover. No translateY lift.
        </p>
      </Section>

      <Section label="Spring Animations">
        <p style={body}>
          Tab pills and selection indicators use spring physics via{' '}
          <code
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 13,
              background: 'var(--color-surface-muted)',
              padding: '2px 6px',
              borderRadius: 4,
            }}
          >
            motion
          </code>{' '}
          —{' '}
          <code
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 13,
              background: 'var(--color-surface-muted)',
              padding: '2px 6px',
              borderRadius: 4,
            }}
          >
            stiffness: 380, damping: 30
          </code>{' '}
          for snappy movement with a natural settle.
        </p>
      </Section>

      <Section label="Glass Blur">
        <p style={body}>
          Surfaces use{' '}
          <code
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 13,
              background: 'var(--color-surface-muted)',
              padding: '2px 6px',
              borderRadius: 4,
            }}
          >
            blur(16px) saturate(1.3)
          </code>{' '}
          with a translucent background. The inner highlight (
          <code
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 13,
              background: 'var(--color-surface-muted)',
              padding: '2px 6px',
              borderRadius: 4,
            }}
          >
            inset 0 0.5px 0
          </code>
          ) gives depth against the frosted surface.
        </p>
      </Section>

      <Section label="Inline Alerts">
        <div className="flex flex-col gap-3">
          <InlineAlert
            variant="info"
            message="Your next revision session is scheduled for tomorrow at 10:00 AM."
          />
          <InlineAlert
            variant="warning"
            message="You haven't studied in 3 days — your streak is at risk!"
          />
          <InlineAlert
            variant="success"
            message="Congratulations! You've completed the Algorithms course."
          />
        </div>
      </Section>
    </>
  );
}
